import { useState } from "react";
import api, { MAD, apiError } from "../lib/api";
import { useData } from "../hooks/useData";
import { useAuth } from "../context/AuthContext";
import { usePos } from "../context/PosContext";
import { PageHeader, ExportButtons, Loader, Empty, StatusBadge, STATUTS } from "../components/ui-kit";
import { Modal, Field } from "../components/Modal";
import { exportExcel, exportPDF } from "../lib/exporters";
import { Plus, Trash2, X } from "lucide-react";
import { toast } from "sonner";

export default function Orders() {
  const { isAdmin, can } = useAuth();
  const { writePosId } = usePos();
  const { data, loading, refetch } = useData("/orders");
  const { data: products } = useData("/products");
  const [open, setOpen] = useState(false);
  const [head, setHead] = useState({ client_nom: "", client_telephone: "", date_livraison: "", note: "" });
  const [cart, setCart] = useState([]);
  const [sel, setSel] = useState("");
  const [qty, setQty] = useState(1);

  const openNew = () => {
    setHead({ client_nom: "", client_telephone: "", date_livraison: "", note: "" });
    setCart([]); setSel(products[0]?.id || ""); setQty(1); setOpen(true);
  };

  const addItem = () => {
    const p = products.find((x) => x.id === sel);
    if (!p || qty < 1) return;
    setCart([...cart, { product_id: p.id, nom: p.nom, quantite: Number(qty), prix_unitaire: p.prix_vente }]);
    setQty(1);
  };

  const cartTotal = cart.reduce((a, i) => a + i.quantite * i.prix_unitaire, 0);

  const save = async (e) => {
    e.preventDefault();
    if (cart.length === 0) return toast.error("Ajoutez au moins un produit");
    if (!writePosId) return toast.error("Sélectionnez un point de vente (en haut) avant d'enregistrer");
    try {
      await api.post(`/orders?pos_id=${writePosId}`, { ...head, items: cart, statut: "en_attente" });
      toast.success("Commande créée"); setOpen(false); refetch();
    } catch (e2) { toast.error(apiError(e2.response?.data?.detail)); }
  };

  const setStatut = async (o, statut) => {
    await api.put(`/orders/${o.id}/statut?statut=${statut}`);
    refetch();
  };

  const remove = async (o) => {
    if (!window.confirm("Supprimer cette commande ?")) return;
    await api.delete(`/orders/${o.id}`); toast.success("Supprimée"); refetch();
  };

  const fmt = (s) => (s ? new Date(s).toLocaleString("fr-FR", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" }) : "—");

  const cols = [
    { header: "Client", value: (r) => r.client_nom },
    { header: "Téléphone", value: (r) => r.client_telephone || "" },
    { header: "Articles", value: (r) => r.items.map((i) => `${i.quantite}× ${i.nom}`).join(", ") },
    { header: "Total (DH)", value: (r) => r.total },
    { header: "Statut", value: (r) => STATUTS[r.statut]?.label || r.statut },
  ];

  return (
    <div>
      <PageHeader title="Commandes clients" subtitle="Gestion des commandes et suivi de leur statut.">
        {can("commandes", "export") && <ExportButtons onExcel={() => exportExcel("commandes_tiramessi", cols, data)} onPDF={() => exportPDF("Commandes TIRAMESSI", cols, data)} />}
        {can("commandes", "create") && <button className="tm-btn" onClick={openNew} data-testid="add-order-btn"><Plus size={16} /> Nouvelle commande</button>}
      </PageHeader>

      {loading ? <Loader /> : data.length === 0 ? <Empty text="Aucune commande." /> : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4" data-testid="orders-grid">
          {data.map((o) => (
            <div key={o.id} className="tm-card p-5 tm-fade" data-testid={`order-card-${o.id}`}>
              <div className="flex items-start justify-between mb-2">
                <div>
                  <p className="font-semibold text-lg">{o.client_nom}</p>
                  {o.client_telephone && <p className="text-xs text-[var(--tm-muted)]">{o.client_telephone}</p>}
                </div>
                <StatusBadge statut={o.statut} />
              </div>
              <div className="space-y-1 my-3 text-sm">
                {o.items.map((i, x) => (
                  <div key={x} className="flex justify-between text-[var(--tm-muted)]">
                    <span>{i.quantite}× {i.nom}</span><span className="tabular">{MAD(i.quantite * i.prix_unitaire)}</span>
                  </div>
                ))}
              </div>
              <div className="flex justify-between font-semibold pt-2 mb-3" style={{ borderTop: "1px solid var(--tm-border)" }}>
                <span>Total</span><span className="tabular" style={{ color: "var(--tm-gold)" }}>{MAD(o.total)}</span>
              </div>
              {o.date_livraison && <p className="text-xs text-[var(--tm-muted)] mb-3">Livraison : {fmt(o.date_livraison)}</p>}
              <div className="flex items-center gap-2">
                {can("commandes", "edit") ? (
                  <select className="tm-input text-xs py-1.5" value={o.statut} onChange={(e) => setStatut(o, e.target.value)} data-testid={`order-status-${o.id}`}>
                    {Object.keys(STATUTS).map((k) => <option key={k} value={k}>{STATUTS[k].label}</option>)}
                  </select>
                ) : <div className="flex-1" />}
                {can("commandes", "delete") && <button onClick={() => remove(o)} className="text-[var(--tm-muted)] hover:text-[#F44336] shrink-0"><Trash2 size={16} /></button>}
              </div>
            </div>
          ))}
        </div>
      )}

      <Modal open={open} onClose={() => setOpen(false)} title="Nouvelle commande" testid="order-modal">
        <form onSubmit={save}>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Nom du client"><input className="tm-input" required value={head.client_nom} onChange={(e) => setHead({ ...head, client_nom: e.target.value })} data-testid="order-client" /></Field>
            <Field label="Téléphone"><input className="tm-input" value={head.client_telephone} onChange={(e) => setHead({ ...head, client_telephone: e.target.value })} /></Field>
          </div>
          <Field label="Date de livraison"><input type="datetime-local" className="tm-input" value={head.date_livraison} onChange={(e) => setHead({ ...head, date_livraison: e.target.value })} /></Field>

          <div className="flex gap-2 items-end mb-3">
            <div className="flex-1">
              <span className="block text-xs uppercase tracking-wider text-[var(--tm-muted)] mb-1.5">Produit</span>
              <select className="tm-input" value={sel} onChange={(e) => setSel(e.target.value)} data-testid="order-product">
                {products.map((p) => <option key={p.id} value={p.id}>{p.nom} — {MAD(p.prix_vente)}</option>)}
              </select>
            </div>
            <div className="w-20">
              <span className="block text-xs uppercase tracking-wider text-[var(--tm-muted)] mb-1.5">Qté</span>
              <input type="number" min="1" className="tm-input" value={qty} onChange={(e) => setQty(e.target.value)} data-testid="order-qty" />
            </div>
            <button type="button" className="tm-btn-ghost tm-btn" onClick={addItem} data-testid="order-add-item"><Plus size={16} /></button>
          </div>

          {cart.length > 0 && (
            <div className="tm-card p-3 mb-4" style={{ background: "var(--tm-bg)" }}>
              {cart.map((i, idx) => (
                <div key={idx} className="flex items-center justify-between py-1.5 text-sm">
                  <span>{i.quantite}× {i.nom}</span>
                  <span className="flex items-center gap-3">
                    <span className="tabular">{MAD(i.quantite * i.prix_unitaire)}</span>
                    <button type="button" onClick={() => setCart(cart.filter((_, x) => x !== idx))} className="text-[var(--tm-muted)] hover:text-[#F44336]"><X size={14} /></button>
                  </span>
                </div>
              ))}
              <div className="flex justify-between pt-2 mt-1 font-semibold" style={{ borderTop: "1px solid var(--tm-border)" }}>
                <span>Total</span><span className="tabular" style={{ color: "var(--tm-gold)" }}>{MAD(cartTotal)}</span>
              </div>
            </div>
          )}

          <Field label="Note"><input className="tm-input" value={head.note} onChange={(e) => setHead({ ...head, note: e.target.value })} /></Field>
          <button type="submit" className="tm-btn w-full mt-2" data-testid="order-save">Créer la commande</button>
        </form>
      </Modal>
    </div>
  );
}
